/**
 * Interaction list state machine - replaces useInteractionStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type {
  Interaction,
  InteractionMessage,
  InteractionNote,
  InteractionActivity,
  InteractionFilters,
  InteractionStatistics,
} from '../types'
import type { TicketAnalysis } from '@repo/ai-analytics'

const initialFilters: InteractionFilters = {
  status: undefined,
  type: undefined,
  priority: undefined,
  assignedTo: undefined,
  queueId: undefined,
  tags: undefined,
  searchQuery: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  hasSLAWarning: undefined,
  hasSLAExpired: undefined,
  unassignedOnly: undefined,
}

const initialStatistics: InteractionStatistics = {
  total: 0,
  active: 0,
  waiting: 0,
  closed: 0,
  transferred: 0,
  byType: { chat: 0, call: 0, email: 0 },
  byPriority: { urgent: 0, high: 0, normal: 0, low: 0 },
  sla: { withinSLA: 0, atRisk: 0, breached: 0 },
  averageResponseTime: 0,
  averageHandleTime: 0,
  averageWaitTime: 0,
}

export interface InteractionListContext {
  interactions: Interaction[]
  messages: Record<string, InteractionMessage[]>
  notes: Record<string, InteractionNote[]>
  activities: Record<string, InteractionActivity[]>
  analysisCache: Record<string, TicketAnalysis>
  analyzingIds: string[]
  selectedInteractionId: string | null
  filters: InteractionFilters
  isLoading: boolean
  error: string | null
  statistics: InteractionStatistics | null
}

export type InteractionListEvent =
  | { type: 'SET_INTERACTIONS'; interactions: Interaction[] }
  | { type: 'ADD_INTERACTION'; interaction: Interaction }
  | { type: 'UPDATE_INTERACTION'; id: string; updates: Partial<Interaction> }
  | { type: 'REMOVE_INTERACTION'; id: string }
  | { type: 'SET_MESSAGES'; interactionId: string; messages: InteractionMessage[] }
  | { type: 'ADD_MESSAGE'; message: InteractionMessage }
  | { type: 'MARK_MESSAGES_AS_READ'; interactionId: string }
  | { type: 'SET_NOTES'; interactionId: string; notes: InteractionNote[] }
  | { type: 'ADD_NOTE'; note: InteractionNote }
  | { type: 'UPDATE_NOTE'; id: string; content: string }
  | { type: 'REMOVE_NOTE'; id: string }
  | { type: 'SET_ACTIVITIES'; interactionId: string; activities: InteractionActivity[] }
  | { type: 'ADD_ACTIVITY'; activity: InteractionActivity }
  | { type: 'SET_ANALYSIS_RESULT'; interactionId: string; analysis: TicketAnalysis }
  | { type: 'SET_ANALYZING'; interactionId: string; analyzing: boolean }
  | { type: 'SET_SELECTED_INTERACTION_ID'; id: string | null }
  | { type: 'SET_FILTERS'; filters: Partial<InteractionFilters> }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_STATISTICS'; statistics: InteractionStatistics }
  | { type: 'RESET' }

const machineSetup = setup({
  types: {
    context: {} as InteractionListContext,
    events: {} as InteractionListEvent,
  },
})

export const interactionListMachine = machineSetup.createMachine({
  id: 'interactionList',
  context: {
    interactions: [],
    messages: {},
    notes: {},
    activities: {},
    analysisCache: {},
    analyzingIds: [],
    selectedInteractionId: null,
    filters: initialFilters,
    isLoading: false,
    error: null,
    statistics: initialStatistics,
  },
  on: {
    SET_INTERACTIONS: {
      actions: assign({
        interactions: ({ event }) => (event.type === 'SET_INTERACTIONS' ? event.interactions : []),
      }),
    },
    ADD_INTERACTION: {
      actions: assign({
        interactions: ({ context, event }) =>
          event.type === 'ADD_INTERACTION'
            ? [event.interaction, ...context.interactions]
            : context.interactions,
      }),
    },
    UPDATE_INTERACTION: {
      actions: assign({
        interactions: ({ context, event }) => {
          if (event.type !== 'UPDATE_INTERACTION') return context.interactions
          return context.interactions.map((i) =>
            i.id === event.id ? { ...i, ...event.updates } : i
          )
        },
      }),
    },
    REMOVE_INTERACTION: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REMOVE_INTERACTION') return {}
        const { [event.id]: _m, ...restMessages } = context.messages
        const { [event.id]: _n, ...restNotes } = context.notes
        const { [event.id]: _a, ...restActivities } = context.activities
        return {
          interactions: context.interactions.filter((i) => i.id !== event.id),
          messages: restMessages,
          notes: restNotes,
          activities: restActivities,
          selectedInteractionId:
            context.selectedInteractionId === event.id ? null : context.selectedInteractionId,
        }
      }),
    },
    SET_MESSAGES: {
      actions: assign({
        messages: ({ context, event }) => {
          if (event.type !== 'SET_MESSAGES') return context.messages
          return { ...context.messages, [event.interactionId]: event.messages }
        },
      }),
    },
    ADD_MESSAGE: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'ADD_MESSAGE') return {}
        const interactionId =
          event.message.interactionId ||
          (event.message as { interactionID?: string }).interactionID ||
          ''
        const msgs = context.messages[interactionId] || []
        const newMsgs = [...msgs, event.message]
        const unreadDelta = event.message.senderRole === 'customer' ? 1 : 0
        const newInteractions = context.interactions.map((i) => {
          if (i.id !== interactionId) return i
          return {
            ...i,
            lastMessage: event.message.content,
            lastMessageAt: event.message.timestamp,
            unreadCount: (i.unreadCount || 0) + unreadDelta,
          }
        })
        return {
          messages: { ...context.messages, [interactionId]: newMsgs },
          interactions: newInteractions,
        }
      }),
    },
    MARK_MESSAGES_AS_READ: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'MARK_MESSAGES_AS_READ') return {}
        const msgs = context.messages[event.interactionId]
        const newMsgs = msgs ? msgs.map((m) => ({ ...m, isRead: true })) : []
        const newInteractions = context.interactions.map((i) =>
          i.id === event.interactionId ? { ...i, unreadCount: 0 } : i
        )
        return {
          messages: msgs
            ? { ...context.messages, [event.interactionId]: newMsgs }
            : context.messages,
          interactions: newInteractions,
        }
      }),
    },
    SET_NOTES: {
      actions: assign({
        notes: ({ context, event }) => {
          if (event.type !== 'SET_NOTES') return context.notes
          return { ...context.notes, [event.interactionId]: event.notes }
        },
      }),
    },
    ADD_NOTE: {
      actions: assign({
        notes: ({ context, event }) => {
          if (event.type !== 'ADD_NOTE') return context.notes
          const intId = event.note.interactionId
          const existing = context.notes[intId] || []
          return {
            ...context.notes,
            [intId]: [...existing, event.note],
          }
        },
      }),
    },
    UPDATE_NOTE: {
      actions: assign({
        notes: ({ context, event }) => {
          if (event.type !== 'UPDATE_NOTE') return context.notes
          const newNotes: Record<string, InteractionNote[]> = {}
          for (const [intId, notes] of Object.entries(context.notes)) {
            newNotes[intId] = notes.map((n) =>
              n.id === event.id ? { ...n, content: event.content, updatedAt: new Date() } : n
            )
          }
          return newNotes
        },
      }),
    },
    REMOVE_NOTE: {
      actions: assign({
        notes: ({ context, event }) => {
          if (event.type !== 'REMOVE_NOTE') return context.notes
          const newNotes: Record<string, InteractionNote[]> = {}
          for (const [intId, notes] of Object.entries(context.notes)) {
            newNotes[intId] = notes.filter((n) => n.id !== event.id)
          }
          return newNotes
        },
      }),
    },
    SET_ACTIVITIES: {
      actions: assign({
        activities: ({ context, event }) => {
          if (event.type !== 'SET_ACTIVITIES') return context.activities
          return { ...context.activities, [event.interactionId]: event.activities }
        },
      }),
    },
    ADD_ACTIVITY: {
      actions: assign({
        activities: ({ context, event }) => {
          if (event.type !== 'ADD_ACTIVITY') return context.activities
          const intId = event.activity.interactionId
          const existing = context.activities[intId] || []
          return {
            ...context.activities,
            [intId]: [event.activity, ...existing],
          }
        },
      }),
    },
    SET_ANALYSIS_RESULT: {
      actions: assign({
        analysisCache: ({ context, event }) => {
          if (event.type !== 'SET_ANALYSIS_RESULT') return context.analysisCache
          return { ...context.analysisCache, [event.interactionId]: event.analysis }
        },
        analyzingIds: ({ context, event }) => {
          if (event.type !== 'SET_ANALYSIS_RESULT') return context.analyzingIds
          return context.analyzingIds.filter((id) => id !== event.interactionId)
        },
      }),
    },
    SET_ANALYZING: {
      actions: assign({
        analyzingIds: ({ context, event }) => {
          if (event.type !== 'SET_ANALYZING') return context.analyzingIds
          if (event.analyzing) {
            return [
              ...context.analyzingIds.filter((id) => id !== event.interactionId),
              event.interactionId,
            ]
          }
          return context.analyzingIds.filter((id) => id !== event.interactionId)
        },
      }),
    },
    SET_SELECTED_INTERACTION_ID: {
      actions: assign({
        selectedInteractionId: ({ event }) =>
          event.type === 'SET_SELECTED_INTERACTION_ID' ? event.id : null,
      }),
    },
    SET_FILTERS: {
      actions: assign({
        filters: ({ context, event }) => {
          if (event.type !== 'SET_FILTERS') return context.filters
          return { ...context.filters, ...event.filters }
        },
      }),
    },
    CLEAR_FILTERS: {
      actions: assign({ filters: initialFilters }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    SET_ERROR: {
      actions: assign({
        error: ({ event }) => (event.type === 'SET_ERROR' ? event.error : null),
      }),
    },
    SET_STATISTICS: {
      actions: assign({
        statistics: ({ event }) => (event.type === 'SET_STATISTICS' ? event.statistics : null),
      }),
    },
    RESET: {
      actions: assign({
        interactions: [],
        messages: {},
        notes: {},
        activities: {},
        analysisCache: {},
        analyzingIds: [],
        selectedInteractionId: null,
        filters: initialFilters,
        isLoading: false,
        error: null,
        statistics: initialStatistics,
      }),
    },
  },
})
